/**
 * @fileoverview
 * Types corresponding to rjsf schemas for EchoShip integration action steps
 * Includes schemas for action step inputs and results
 */

import * as yup from 'yup'

const EMAIL_LIST_PATTERN = /^([\w.-]+@[\w.-]+\.[a-zA-Z]{2,})(;[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}){0,4}$/

const semicolonEmailList = yup
  .string()
  .max(300)
  .matches(new RegExp(EMAIL_LIST_PATTERN), 'Must be 1-5 valid emails separated by semicolons')

// Utility type to extract all values from nested const objects
type ValuesOf<T extends Record<string, readonly unknown[]>> = T[keyof T][number]

export enum AccessorialCode {
  LIFT_GATE_REQUIRED = 'LIFTGATEREQUIRED',
  INSIDE_PICKUP = 'INSIDEPICKUP',
  HAZARDOUS_MATERIALS = 'HAZARDOUSMATERIALS',
  SINGLE_SHIPMENT = 'SINGLESHIPMENT',
  LIMITED_ACCESS_FEE = 'LIMITEDACCESSFEE',
  PROTECT_FROM_FREEZING = 'PROTECTFROMFREEZING',
  EXTREME_LENGTH = 'EXTREMELENGTH',
  EXTREME_LENGTH_8_TO_12 = 'EXTREMELENGTH8TO12',
  EXTREME_LENGTH_12_TO_16 = 'EXTREMELENGTH12TO16',
  EXTREME_LENGTH_20_AND_OVER = 'EXTREMELENGTH20ANDOVER',
  INSIDE_DELIVERY = 'INSIDEDELIVERY',
  NOTIFY_PRIOR_TO_DELIVERY = 'NOTIFYPRIORTODELIVERY',
  SORT_AND_SEGREGATE = 'SORTANDSEGREGATE',
  APPOINTMENT_FEE = 'APPOINTMENTFEE',
}

export enum LocationType {
  BUSINESS = 'BUSINESS',
  RESIDENTIAL = 'RESIDENTIAL',
  CONSTRUCTION_SITE = 'CONSTRUCTIONSITE',
  TRADE_SHOW = 'TRADESHOW',
}
export const LOCATION_TYPE_VALUES = Object.values(LocationType)

export enum StopType {
  PICKUP = 'PICK',
  DROP = 'DROP',
}

// Origin Accessorials
export const ACCESSORIALS_ORIGIN = {
  [LocationType.BUSINESS]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_PICKUP,
    AccessorialCode.HAZARDOUS_MATERIALS,
    AccessorialCode.SINGLE_SHIPMENT,
    AccessorialCode.LIMITED_ACCESS_FEE,
    AccessorialCode.PROTECT_FROM_FREEZING,
    AccessorialCode.EXTREME_LENGTH,
    AccessorialCode.EXTREME_LENGTH_8_TO_12,
    AccessorialCode.EXTREME_LENGTH_12_TO_16,
    AccessorialCode.EXTREME_LENGTH_20_AND_OVER,
  ],
  [LocationType.CONSTRUCTION_SITE]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_PICKUP,
    AccessorialCode.HAZARDOUS_MATERIALS,
    AccessorialCode.SINGLE_SHIPMENT,
    AccessorialCode.PROTECT_FROM_FREEZING,
    AccessorialCode.EXTREME_LENGTH,
    AccessorialCode.EXTREME_LENGTH_8_TO_12,
    AccessorialCode.EXTREME_LENGTH_12_TO_16,
    AccessorialCode.EXTREME_LENGTH_20_AND_OVER,
  ],
  [LocationType.RESIDENTIAL]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_PICKUP,
    AccessorialCode.HAZARDOUS_MATERIALS,
    AccessorialCode.SINGLE_SHIPMENT,
    AccessorialCode.PROTECT_FROM_FREEZING,
    AccessorialCode.EXTREME_LENGTH,
    AccessorialCode.EXTREME_LENGTH_8_TO_12,
    AccessorialCode.EXTREME_LENGTH_12_TO_16,
    AccessorialCode.EXTREME_LENGTH_20_AND_OVER,
  ],
  [LocationType.TRADE_SHOW]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_PICKUP,
    AccessorialCode.HAZARDOUS_MATERIALS,
    AccessorialCode.SINGLE_SHIPMENT,
    AccessorialCode.PROTECT_FROM_FREEZING,
    AccessorialCode.EXTREME_LENGTH,
    AccessorialCode.EXTREME_LENGTH_8_TO_12,
    AccessorialCode.EXTREME_LENGTH_12_TO_16,
    AccessorialCode.EXTREME_LENGTH_20_AND_OVER,
  ],
}
export type OriginAccessorial = ValuesOf<typeof ACCESSORIALS_ORIGIN>

// Destination Accessorials
export const ACCESSORIALS_DESTINATION = {
  [LocationType.BUSINESS]: [
    AccessorialCode.INSIDE_DELIVERY,
    AccessorialCode.NOTIFY_PRIOR_TO_DELIVERY,
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.LIMITED_ACCESS_FEE,
    AccessorialCode.SORT_AND_SEGREGATE,
    AccessorialCode.APPOINTMENT_FEE,
  ],
  [LocationType.CONSTRUCTION_SITE]: [
    AccessorialCode.INSIDE_DELIVERY,
    AccessorialCode.NOTIFY_PRIOR_TO_DELIVERY,
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.SORT_AND_SEGREGATE,
    AccessorialCode.APPOINTMENT_FEE,
  ],
  [LocationType.RESIDENTIAL]: [
    AccessorialCode.INSIDE_DELIVERY,
    AccessorialCode.NOTIFY_PRIOR_TO_DELIVERY,
    AccessorialCode.LIFT_GATE_REQUIRED,
  ],
  [LocationType.TRADE_SHOW]: [
    AccessorialCode.INSIDE_DELIVERY,
    AccessorialCode.NOTIFY_PRIOR_TO_DELIVERY,
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.SORT_AND_SEGREGATE,
    AccessorialCode.APPOINTMENT_FEE,
  ],
}
export type DestinationAccessorial = ValuesOf<typeof ACCESSORIALS_DESTINATION>

// TL/PL Accessorials
export const ACCESSORIALS_TLPL = {
  [StopType.PICKUP]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_PICKUP,
    AccessorialCode.HAZARDOUS_MATERIALS,
    AccessorialCode.SINGLE_SHIPMENT,
  ],
  [StopType.DROP]: [
    AccessorialCode.LIFT_GATE_REQUIRED,
    AccessorialCode.INSIDE_DELIVERY,
    AccessorialCode.NOTIFY_PRIOR_TO_DELIVERY,
  ],
}
export type TLPLAccessorial = ValuesOf<typeof ACCESSORIALS_TLPL>

export enum PalletType {
  CORRUGATED = 'CORRUGATED',
  GAYLORD = 'GAYLORD',
  METAL = 'METAL',
  PLASTIC = 'PLASTIC',
  WOOD = 'WOOD',
  OTHER = 'OTHER',
}
export const PALLET_TYPE_VALUES = Object.values(PalletType)

export enum CarrierGuaranteeType {
  GN = 'GN',
  G1PM = 'G1PM',
  G2PM = 'G2PM',
  G3PM = 'G3PM',
  G4PM = 'G4PM',
  G5PM = 'G5PM',
  G8AM = 'G8AM',
  G9AM = 'G9AM',
  G10AM = 'G10AM',
  G11AM = 'G11AM',
  G12PM = 'G12PM',
  G330PM = 'G330PM',
}
export const CARRIER_GUARANTEE_TYPE_VALUES = Object.values(CarrierGuaranteeType)

export enum PackageType {
  BAG = 'BAG',
  BOXES = 'BOXES',
  BUNDLES = 'BUNDLES',
  CANS = 'CANS',
  CARTONS = 'CARTONS',
  CASE = 'CASE',
  CRATE = 'CRATE',
  DRUMS = 'DRUMS',
  EA = 'EA',
  FIBERDRUMS = 'FIBERDRUMS',
  FLATRACK = 'FLATRACK',
  KEGS = 'KEGS',
  OTHER = 'OTHER',
  PAILS = 'PAILS',
  PALLETS = 'PALLETS',
  PIECES = 'PIECES',
  REELS = 'REELS',
  ROLLS = 'ROLLS',
  TOTE = 'TOTE',
  TUBES = 'TUBES',
}
export const PACKAGE_TYPE_VALUES = Object.values(PackageType)

export enum HandlingUnitType {
  CORRUGATED = 'CORRUGATED',
  CRATES = 'CRATES',
  DRUMS = 'DRUMS',
  FLOOR = 'FLOOR',
  GAYLORDS = 'GAYLORDS',
  PALLETS = 'PALLETS',
  ROLLS = 'ROLLS',
  SKIDS = 'SKIDS',
  BALES = 'BALES',
}
export const HANDLING_UNIT_TYPE_VALUES = Object.values(HandlingUnitType)

export enum UnitOfWeight {
  LB = 'LB',
  KG = 'KG',
}
export const UNIT_OF_WEIGHT_VALUES = Object.values(UnitOfWeight)

/** NMFC Classes for Echo Ship */
export enum NmfcClass {
  CLASS_50 = '50',
  CLASS_55 = '55',
  CLASS_60 = '60',
  CLASS_65 = '65',
  CLASS_70 = '70',
  CLASS_77_5 = '77.5',
  CLASS_85 = '85',
  CLASS_92_5 = '92.5',
  CLASS_100 = '100',
  CLASS_110 = '110',
  CLASS_125 = '125',
  CLASS_150 = '150',
  CLASS_175 = '175',
  CLASS_200 = '200',
  CLASS_250 = '250',
  CLASS_300 = '300',
  CLASS_400 = '400',
}
export const NMFC_CLASS_VALUES = Object.values(NmfcClass)

/** Action step input schema for EchoShip - Create Quote */
export const echoShipCreateQuoteActionStepInputSchema = yup.object({
  OriginPostalCode: yup.string().required(),
  OriginCountryCode: yup.string().oneOf(['US', 'CA']).required(),
  OriginAccessorials: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([
          'LIFTGATEREQUIRED',
          'INSIDEPICKUP',
          'HAZARDOUSMATERIALS',
          'SINGLESHIPMENT',
          'LIMITEDACCESSFEE',
          'PROTECTFROMFREEZING',
          'EXTREMELENGTH',
          'RESIDENTIALPICKUP',
        ])
        .optional()
        .nullable(),
    )
    .optional()
    .nullable(),
  DestinationPostalCode: yup.string().required(),
  DestinationCountryCode: yup.string().oneOf(['US', 'CA']).required(),
  DestinationAccessorials: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([
          'INSIDEDELIVERY',
          'NOTIFYPRIORTODELIVERY',
          'LIFTGATEREQUIRED',
          'LIMITEDACCESSFEE',
          'SORTANDSEGREGATE',
          'RESIDENTIALDELIVERY',
        ])
        .optional()
        .nullable(),
    )
    .optional()
    .nullable(),
  PickUpDate: yup
    .string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Must be a valid date in MM/dd/yyyy format')
    .required(),
  PalletQuantity: yup.number().integer().optional().nullable(),
  UnitOfWeight: yup.string().oneOf(UNIT_OF_WEIGHT_VALUES).required(),
  Items: yup
    .array()
    .of(
      yup.object({
        NmfcClass: yup.string().oneOf(NMFC_CLASS_VALUES).required(),
        Weight: yup.number().integer().required(),
      }),
    )
    .required(),
  AdjustDeliveryByDate: yup.boolean().optional().nullable(),
  RequestedDeliveryBy: yup
    .string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Must be a valid date in MM/dd/yyyy format')
    .optional()
    .nullable(),
  DeliveryByDateLeadDays: yup.number().optional().nullable(),
  OriginType: yup.string().oneOf(LOCATION_TYPE_VALUES).optional().nullable(),
  DestinationType: yup.string().oneOf(LOCATION_TYPE_VALUES).optional().nullable(),
})

/** Echo API Meta object schema */
const echoMetaSchema = yup
  .object({
    String: yup.string().optional().nullable(),
  })
  .optional()
  .nullable()

/** Echo API Error object schema */
const echoErrorSchema = yup.object({
  ErrorCode: yup.string().required(),
  FieldName: yup.string().optional().nullable(),
  Message: yup.string().optional().nullable(),
  Meta: echoMetaSchema,
})

/** EchoShip REST API Response status (error) schema */
const echoShipResponseStatusSchema = yup.object({
  /**
   * Echo Error Code Ranges:
   * EGL4500-EGL4514: Reference Field errors
   * EGL4524-EGL4526: SCAC errors
   * EGL4536-EGL4540: Ship Date errors
   * EGL4549-EGL4578: Shipment Details errors
   * EGL4588-EGL4607: Warehouse Address errors
   */
  ErrorCode: yup.string().optional().nullable(),
  Message: yup.string().optional().nullable(),
  StackTrace: yup.string().optional().nullable(),
  Errors: yup.array().of(echoErrorSchema).optional().nullable(),
  Meta: echoMetaSchema,
})

/** EchoShip Shipment (Carrier) rate object schema */
const echoShipRateDetailsSchema = yup.object({
  CarrierName: yup.string().optional().nullable(),
  CarrierSCAC: yup.string().optional().nullable(),
  CarrierGuarantee: yup.string().optional().nullable(),
  CarrierTransitDays: yup.number().optional().nullable(),
  PointType: yup.string().optional().nullable(),
  OriginTerminalAddress: yup.string().optional().nullable(),
  OriginTerminalCity: yup.string().optional().nullable(),
  OriginTerminalStateProvince: yup.string().optional().nullable(),
  OriginTerminalPostalCode: yup.string().optional().nullable(),
  OriginTerminalContact: yup.string().optional().nullable(),
  OriginTerminalPhone: yup.string().optional().nullable(),
  DestinationTerminalAddress: yup.string().optional().nullable(),
  DestinationTerminalCity: yup.string().optional().nullable(),
  DestinationTerminalStateProvince: yup.string().optional().nullable(),
  DestinationTerminalPostalCode: yup.string().optional().nullable(),
  DestinationTerminalContact: yup.string().optional().nullable(),
  DestinationTerminalPhone: yup.string().optional().nullable(),
  TotalCharge: yup.number().optional().nullable(),
  BaseCharge: yup.number().optional().nullable(),
  MinimumCharge: yup.number().optional().nullable(),
  FuelCharge: yup.number().optional().nullable(),
  FuelSurcharge: yup.number().optional().nullable(),
  AccessorialCharge: yup.number().optional().nullable(),
  Discount: yup.number().optional().nullable(),
  Accessorials: yup
    .array()
    .of(
      yup.object({
        Type: yup.string().optional().nullable(),
        Rate: yup.string().optional().nullable(),
        Charge: yup.number().optional().nullable(),
      }),
    )
    .optional()
    .nullable(),
  ExpirationDate: yup.string().optional().nullable(),
})

/** Action step result schema for EchoShip - CreateQuote */
export const echoShipCreateQuoteResponseSchema = yup.object({
  Rates: yup.array().of(echoShipRateDetailsSchema).optional().nullable(),
  ResponseStatus: echoShipResponseStatusSchema.optional().nullable(),
})

/** Base Location Schema for Echo Ship LTL/TL/PL Tender */
const baseLocationSchema = yup.object({
  LocationType: yup.string().oneOf(LOCATION_TYPE_VALUES).required(),
  LocationName: yup.string().required(),
  AppointmentDate: yup
    .string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Must be MM/dd/yyyy format')
    .required(),
  AppointmentStart: yup
    .string()
    .matches(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, 'Must be MM/DD/YYYY hh:mm format')
    .required(),
  AppointmentEnd: yup
    .string()
    .matches(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, 'Must be MM/DD/YYYY hh:mm format')
    .required(),
  AddressLine1: yup.string().required(),
  AddressLine2: yup.string(),
  City: yup.string().required(),
  StateProvince: yup.string().required(),
  PostalCode: yup.string().required(),
  CountryCode: yup.string().oneOf(['US', 'CA']).required(),
  ContactName: yup.string().required(),
  ContactPhone: yup.string(),
  BolNumber: yup.string(),
  ReferenceNumber: yup.string(),
  IsBlind: yup.boolean(),
  BlindLocationName: yup.string(),
  BlindAddressLine1: yup.string(),
  BlindAddressLine2: yup.string(),
  BlindCity: yup.string(),
  BlindStateProvince: yup.string(),
  BlindPostalCode: yup.string(),
  BlindCountryCode: yup.string(),
  PickupDeliveryNumber: yup.string(),
  AdjustDeliveryByDate: yup.boolean(),
  DeliveryByDateLeadDays: yup.boolean(),
})

function createAccessorialValidator<
  T extends Record<string, readonly string[]>,
  U extends T[keyof T][number],
>(label: string, accessorialMap: T): yup.ArraySchema<string[] | undefined, yup.AnyObject, '', ''> {
  return yup
    .array()
    .of(yup.string().required())
    .test({
      name: `valid-${label}-accessorial`,
      message: `Invalid ${label} accessorial for location type`,
      test(value) {
        if (!value || value.length === 0) {
          return true
        }

        const locationType = this.parent.LocationType as keyof T
        const valid = accessorialMap[locationType]
        if (!valid) {
          return false
        }

        return value.every((a): a is U => valid.includes(a as U))
      },
    })
    .optional()
}

/** Origin Location Schema */
export const originLocationSchema = baseLocationSchema.shape({
  Accessorials: createAccessorialValidator('origin', ACCESSORIALS_ORIGIN),
})

/** Destination Location Schema */
export const destinationLocationSchema = baseLocationSchema.shape({
  Accessorials: createAccessorialValidator('destination', ACCESSORIALS_DESTINATION),
})

/** Item Schema for Echo Ship LTL Tender */
const itemSchema = yup.object({
  ItemId: yup.string(),
  Description: yup.string().required(),
  NmfcClass: yup.string().oneOf(NMFC_CLASS_VALUES).required(),
  NmfcNumber: yup.string().required(),
  Weight: yup.number().integer().required(),
  PackageType: yup.string().oneOf(PACKAGE_TYPE_VALUES).required(),
  PackageQuantity: yup.number().integer().required(),
  HandlingUnitType: yup.string().oneOf(HANDLING_UNIT_TYPE_VALUES).required(),
  HandlingUnitQuantity: yup.number().integer().required(),
  HazardousMaterial: yup.boolean().required(),
})

/** Reference Schema for Echo Ship LTL Tender */
const referenceSchema = yup.object({
  Name: yup.string().required(),
  Value: yup.string().required(),
})

/** Action step input schema for EchoShip - Create LTL Tender */
export const echoShipCreateShipmentLTLActionStepInputSchema = yup.object({
  Origin: originLocationSchema.required(),
  Destination: destinationLocationSchema.required(),
  Items: yup.array().of(itemSchema).required(),
  PalletQuantity: yup.number().integer().optional(),
  PalletType: yup.string().oneOf(PALLET_TYPE_VALUES),
  PalletStackable: yup.boolean(),
  SkidSpotQuantity: yup
    .number()
    .integer()
    .when('PalletStackable', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  UnitOfWeight: yup.string().oneOf(UNIT_OF_WEIGHT_VALUES).required(),
  CustomerNotes: yup.string(),
  ShipmentNotes: yup.string(),
  CarrierSCAC: yup.string(),
  CarrierGuarantee: yup.string().oneOf(CARRIER_GUARANTEE_TYPE_VALUES),
  QuoteId: yup.string(),
  BolNumber: yup.string().required(),
  OrderNumber: yup.string(),
  PoNumber: yup.string(),
  ProNumber: yup.string(),
  PodSignature: yup.string(),
  GlCode: yup.string(),
  AckNotification: semicolonEmailList,
  AsnNotification: semicolonEmailList,
  References: yup.array().of(referenceSchema),
})

/** LTL Tender Response Schema */
export const echoShipCreateShipmentLTLResponseSchema = yup.object({
  ShipmentId: yup.number().optional().nullable(),
  ShipmentMode: yup.string().optional().nullable(),
  ResponseStatus: echoShipResponseStatusSchema.optional().nullable(),
})

/** Type definitions for LTL Tender Request */
export type EchoShipBaseLocation = yup.InferType<typeof baseLocationSchema>
export type EchoShipOriginLocation = yup.InferType<typeof originLocationSchema>
export type EchoShipDestinationLocation = yup.InferType<typeof destinationLocationSchema>
export type EchoShipItem = yup.InferType<typeof itemSchema>
export type EchoShipReference = yup.InferType<typeof referenceSchema>
export type EchoShipCreateShipmentLTLTRequest = yup.InferType<
  typeof echoShipCreateShipmentLTLActionStepInputSchema
>
export type EchoShipCreateShipmentLTLResponse = yup.InferType<
  typeof echoShipCreateShipmentLTLResponseSchema
>
